import axios from "axios";
import { Edit, MoreVertical, Trash2, Building2, Search, X, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import {
  confirmAction,
  showError,
  showSuccess,
} from "../../../utils/toastUtils";


const ITEMS_PER_PAGE = 10;

const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [actionOpen, setActionOpen] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);

  const totalPages = Math.ceil(filteredVendors.length / ITEMS_PER_PAGE);
  const paginatedVendors = filteredVendors.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Fetch vendors
  const fetchVendors = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/vendors`);
      const vendorsData = res?.data?.data || [];
      setVendors(vendorsData);
      setFilteredVendors(vendorsData);
    } catch (err) {
      console.error("Fetch vendors failed", err);
      showError("Failed to load vendors");
      setVendors([]);
      setFilteredVendors([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  // Apply search filter
  useEffect(() => {
    if (searchQuery.trim()) {
      const lower = searchQuery.toLowerCase();
      const filtered = vendors.filter(
        (v) =>
          v.vendorName?.toLowerCase().includes(lower) ||
          v.ownerName?.toLowerCase().includes(lower) ||
          v.email?.toLowerCase().includes(lower) ||
          v.phone?.toLowerCase().includes(lower)
      );
      setFilteredVendors(filtered);
    } else {
      setFilteredVendors(vendors);
    }
    setCurrentPage(1);
  }, [searchQuery, vendors]);

  // Handle delete vendor
  const handleDeleteVendor = async (vendor) => {
    const confirmed = await confirmAction({
      title: "Delete Vendor?",
      text: `This will permanently delete ${vendor.vendorName}.`,
      confirmText: "Yes, Delete",
      cancelText: "Cancel",
      icon: "warning",
    });

    if (!confirmed) return;

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/vendors/${vendor._id}`);
      showSuccess("Vendor deleted successfully");
      setActionOpen(null);
      fetchVendors();
    } catch (err) {
      showError(err?.response?.data?.message || "Delete failed");
    }
  };

  // Handle edit click
  const handleEditClick = (vendor) => {
    setEditingVendor(vendor);
    setShowModal(true);
    setActionOpen(null);
  };

  // Handle add click
  const handleAddClick = () => {
    setEditingVendor(null);
    setShowModal(true);
  };

  // Handle modal success
  const handleModalSuccess = () => {
    setShowModal(false);
    setEditingVendor(null);
    fetchVendors();
  };

  return (
    <div className="flex flex-col bg-gradient-to-br px-6 min-h-screen from-[#FFF7F3] to-gray-50 gap-4 min-w-0">
      <div className="py-2">
        <Breadcrumb title1="Vendors" title2="Vendor Management" />
      </div>

      <div className=" mb-6 bg-white rounded-md shadow-lg p-6">
        {/* FILTERS AND ACTIONS BAR */}
        <div className="flex flex-col gap-4 mb-6">
          {/* Top Row: Search and Add */}
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[300px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search vendors..."
                  className="w-full shadow-md pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor transition"
                />
              </div>

              {/* Clear Search */}
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="px-2 py-1 text-gray-600 hover:text-gray-900 font-medium flex items-center gap-2 transition"
                >
                  <X size={18} />
                  Clear
                </button>
              )}
            </div>

            {/* Add Vendor Button */}
            <button
              onClick={handleAddClick}
              className="px-6 py-2.5 bg-primaryBtnColor text-white font-medium rounded-lg hover:bg-primaryBtnHoverColor transition flex items-center gap-2 active:scale-95"
            >
              <Plus size={18} />
              Add Vendor
            </button>
          </div>

          {/* Results Summary */}
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              Showing{" "}
              <span className="font-medium text-gray-900">
                {filteredVendors.length}
              </span>{" "}
              of{" "}
              <span className="font-medium text-gray-900">
                {vendors.length}
              </span>{" "}
              vendors
            </span>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full min-w-max border border-gray-300">
            <thead>
              <tr className="text-left bg-[#EDB5A4] text-primaryTextColor text-xs uppercase tracking-wider">
                <th className="p-3 text-center">Sl No</th>
                <th className="p-3">Vendor Name</th>
                <th className="p-3">Owner Name</th>
                <th className="p-3">Email</th>
                <th className="p-3">Phone</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>

            {isLoading ? (
              <VendorTableSkeleton rows={ITEMS_PER_PAGE} />
            ) : filteredVendors.length === 0 ? (
              <EmptyState
                hasSearch={searchQuery.trim().length > 0}
                onClearSearch={() => setSearchQuery("")}
              />
            ) : (
              <tbody className="divide-y divide-gray-200 text-sm">
                {paginatedVendors.map((vendor, index) => (
                  <tr key={vendor._id} className="hover:bg-gray-50 transition">
                    <td className="p-3 text-center">
                      {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                    </td>

                    <td className="p-4 font-medium capitalize">
                      {vendor.vendorName}
                    </td>

                    <td className="p-4 capitalize">{vendor.ownerName}</td>

                    <td className="p-4">{vendor.email}</td>

                    <td className="p-4">{vendor.phone}</td>

                    <td className="p-4 text-center">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          vendor.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {vendor.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td className="p-4 text-center relative">
                      <button
                        onClick={() =>
                          setActionOpen((prev) =>
                            prev === vendor._id ? null : vendor._id,
                          )
                        }
                        className="p-2 hover:bg-gray-200 rounded-lg transition"
                      >
                        <MoreVertical size={20} className="text-gray-600" />
                      </button>

                      {actionOpen === vendor._id && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setActionOpen(null)}
                          />

                          <div
                            className={`absolute right-20 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50 ${
                              index >= paginatedVendors?.length - 2
                                ? "bottom-[60%]"
                                : "top-14"
                            }`}
                          >
                            <button
                              onClick={() => handleEditClick(vendor)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center gap-3"
                            >
                              <Edit size={16} />
                              Edit
                            </button>

                            <button
                              onClick={() => handleDeleteVendor(vendor)}
                              className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 flex items-center gap-3"
                            >
                              <Trash2 size={16} />
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <p className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </p>

            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
              >
                Prev
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* CREATE/EDIT MODAL */}
      <VendorModal
        isOpen={showModal}
        vendor={editingVendor}
        onClose={() => {
          setShowModal(false);
          setEditingVendor(null);
        }}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

// VENDOR MODAL COMPONENT
const VendorModal = ({ isOpen, vendor, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    vendorName: "",
    ownerName: "",
    email: "",
    phone: "",
    isActive: true,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (vendor) {
      setFormData({
        vendorName: vendor.vendorName || "",
        ownerName: vendor.ownerName || "",
        email: vendor.email || "",
        phone: vendor.phone || "",
        isActive: vendor.isActive ?? true,
      });
    } else {
      setFormData({
        vendorName: "",
        ownerName: "",
        email: "",
        phone: "",
        isActive: true,
      });
    }
    setErrors({});
  }, [vendor, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.vendorName.trim()) {
      newErrors.vendorName = "Vendor name is required";
    }

    if (!formData.ownerName.trim()) {
      newErrors.ownerName = "Owner name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Phone must be 10 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      if (vendor) {
        // Update
        await axios.put(
          `${process.env.REACT_APP_API_URL}/vendors/${vendor._id}`,
          formData
        );
        showSuccess("Vendor updated successfully");
      } else {
        // Create
        await axios.post(`${process.env.REACT_APP_API_URL}/vendors`, formData);
        showSuccess("Vendor created successfully");
      }
      onSuccess();
    } catch (err) {
      showError(err?.response?.data?.message || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-primaryColor via-brandAccentColor to-primaryColor">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Building2 size={20} className="text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-white">
                    {vendor ? "Edit Vendor" : "Add New Vendor"}
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition active:scale-95"
                >
                  <X size={18} className="text-white" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
                <div className="space-y-4">
                  {/* Vendor Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vendor Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="vendorName"
                      value={formData.vendorName}
                      onChange={handleChange}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition ${errors.vendorName
                        ? "border-red-300 focus:ring-red-200"
                        : "border-gray-300 focus:ring-blue-200"
                        }`}
                      placeholder="Enter vendor name"
                    />
                    {errors.vendorName && (
                      <p className="mt-1 text-xs text-red-600">{errors.vendorName}</p>
                    )}
                  </div>

                  {/* Owner Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Owner Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="ownerName"
                      value={formData.ownerName}
                      onChange={handleChange}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition ${errors.ownerName
                        ? "border-red-300 focus:ring-red-200"
                        : "border-gray-300 focus:ring-blue-200"
                        }`}
                      placeholder="Enter owner name"
                    />
                    {errors.ownerName && (
                      <p className="mt-1 text-xs text-red-600">{errors.ownerName}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition ${errors.email
                        ? "border-red-300 focus:ring-red-200"
                        : "border-gray-300 focus:ring-blue-200"
                        }`}
                      placeholder="vendor@example.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      maxLength={10}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition ${errors.phone
                        ? "border-red-300 focus:ring-red-200"
                        : "border-gray-300 focus:ring-blue-200"
                        }`}
                      placeholder="10 digit phone number"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-xs text-red-600">{errors.phone}</p>
                    )}
                  </div>

                  {/* Status */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isActive"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-200"
                    />
                    <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                      Active
                    </label>
                  </div>
                </div>
              </form>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition active:scale-95"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-6 py-2.5 bg-primaryBtnColor text-white font-medium rounded-lg hover:bg-primaryBtnHoverColor transition active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {vendor ? "Updating..." : "Creating..."}
                    </span>
                  ) : (
                    <span>{vendor ? "Update Vendor" : "Create Vendor"}</span>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// SKELETON LOADER
const VendorTableSkeleton = ({ rows = 10 }) => (
  <tbody>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i} className="animate-pulse">
        {Array.from({ length: 7 }).map((_, j) => (
          <td key={j} className="p-3">
            <div className="h-8 bg-gray-200 rounded" />
          </td>
        ))}
      </tr>
    ))}
  </tbody>
);

// EMPTY STATE
const EmptyState = ({ hasSearch, onClearSearch }) => (
  <tbody>
    <tr>
      <td colSpan={7} className="py-16 text-center">
        <div className="flex flex-col items-center gap-4 text-gray-500">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
            <Building2 className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700">
            {hasSearch ? "No vendors match your search" : "No vendors found"}
          </h3>
          {hasSearch && (
            <button
              onClick={onClearSearch}
              className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
            >
              Clear Search
            </button>
          )}
        </div>
      </td>
    </tr>
  </tbody>
);

export default Vendors;